import { z } from 'zod';
import { analyzeSms, toGreekGsmCapitals } from '../sms';
import { EmailDesignSchema } from '../emailDesign';

export const CHANNELS = ['sms', 'email'] as const;
export type Channel = (typeof CHANNELS)[number];
export const ChannelSchema = z.enum(CHANNELS);

export const DELIVERY_PURPOSES = [
  'review_request',
  'review_reminder',
  'campaign',
  'automation',
  'test',
] as const;
export type DeliveryPurpose = (typeof DELIVERY_PURPOSES)[number];

/** Spec §10.3 */
export const DELIVERY_STATUSES = [
  'draft',
  'queued',
  'reserved',
  'dispatching',
  'accepted',
  'sent',
  'delivered',
  'bounced',
  'failed',
  'suppressed',
  'unsubscribed',
  'unknown',
  'cancelled',
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Terminal statuses never transition further (except via explicit operator reconciliation). */
export const TERMINAL_DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'delivered',
  'bounced',
  'failed',
  'suppressed',
  'unsubscribed',
  'cancelled',
];

export const DELIVERY_EVENT_TYPES = [
  'queued',
  'reserved',
  'dispatching',
  'accepted',
  'sent',
  'delivered',
  'bounced',
  'failed',
  'opened',
  'clicked',
  'unsubscribed',
  'complained',
  'suppressed',
  'cancelled',
  'unknown',
] as const;
export type DeliveryEventType = (typeof DELIVERY_EVENT_TYPES)[number];

export const MESSAGE_TEMPLATE_KINDS = [
  'review_invite',
  'review_reminder',
  'birthday',
  'new_year',
  'win_back',
  'thank_you_purchase',
  'appointment_follow_up',
  'membership_expiry',
  'newsletter',
  'offer',
  'announcement',
  'custom',
] as const;
export type MessageTemplateKind = (typeof MESSAGE_TEMPLATE_KINDS)[number];

export const SendTestRequestSchema = z
  .object({
    channel: ChannelSchema,
    to: z.string().trim().min(3).max(254),
    subject: z.string().max(200).optional(),
    body: z.string().max(20000).default(''),
    /** Designed email (email channel). */
    design: EmailDesignSchema.nullable().optional(),
    /** Forms engine: a test of a linked campaign (`{{formLink}}` renders a no-write preview link). */
    formId: z.uuid().optional(),
  })
  .superRefine((v, ctx) => {
    if (!(v.channel === 'email' && v.design) && !v.body.trim())
      ctx.addIssue({ code: 'custom', path: ['body'], message: 'Message is empty' });
  });

export const MergeVariableSchema = z.enum([
  'firstName',
  'lastName',
  'displayName',
  'businessName',
  'senderName',
  'link',
  'unsubscribeLink',
  // Forms engine (FORMS_DESIGN §8.2): valid only in campaigns linked to a form.
  'formLink',
  'formTitle',
  'eventDate',
  'eventTime',
  'eventPlace',
  'calendarLink',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Messaging module contracts (sending engine, campaigns, unsubscribe). Pure helpers below are
// shared by the API (what is sent) and the web composer (live preview), so both render the same.
// ─────────────────────────────────────────────────────────────────────────────

export const MERGE_VARIABLES = MergeVariableSchema.options;
export type MergeVariable = (typeof MERGE_VARIABLES)[number];

/** Variables a free-text campaign may use (`link` belongs to review flows, `unsubscribeLink` is added automatically). */
export const CAMPAIGN_MERGE_VARIABLES = [
  'firstName',
  'lastName',
  'displayName',
  'businessName',
  'senderName',
  'unsubscribeLink',
] as const satisfies readonly MergeVariable[];

/** Extra variables of campaigns linked to a form (`{{formLink}}` is required there). */
export const FORM_MERGE_VARIABLES = [
  'formLink',
  'formTitle',
  'eventDate',
  'eventTime',
  'eventPlace',
  /** Events: the personal `.ics` download (add to calendar). */
  'calendarLink',
] as const satisfies readonly MergeVariable[];

/** Variables a campaign may use: linked campaigns add the form variables. */
export function campaignMergeVariables(linkedToForm: boolean): readonly MergeVariable[] {
  return linkedToForm
    ? [...CAMPAIGN_MERGE_VARIABLES, ...FORM_MERGE_VARIABLES]
    : CAMPAIGN_MERGE_VARIABLES;
}

/** True when a template places `{{formLink}}` (a linked campaign must). */
export function usesFormLink(template: string): boolean {
  return /\{\{\s*formLink\s*(?:\|[^{}]*)?\}\}/.test(template);
}

/** Reasons a recipient is excluded from a send (estimate + delivery `error_code`). */
export const EXCLUSION_REASONS = [
  'no_consent',
  'suppressed',
  'missing_address',
  'invalid_address',
  'inactive_contact',
] as const;
export type ExclusionReason = (typeof EXCLUSION_REASONS)[number];

/** Longest SMS we accept (4 parts: 612 GSM-7 septets / 268 UCS-2 units — smsbox.gr limits). */
export const MAX_SMS_SEGMENTS = 4;
export const MAX_EMAIL_BODY = 20000;

/**
 * Every `{{…}}` token, not only well-formed ones: `{{first_name}}`, `{{όνομα}}` or `{{first-name}}`
 * must be reported as unknown (and never reach a recipient as literal braces).
 */
const MERGE_RE = /\{\{\s*([^{}|]*?)\s*(?:\|([^{}]*))?\}\}/g;

/** Name of a placeholder for reporting (an empty `{{}}` is reported as written). */
function placeholderName(m: RegExpMatchArray): string {
  return m[1] || m[0];
}

/** Merge variables referenced by a template (deduplicated, in order of appearance). */
export function extractMergeVariables(template: string): string[] {
  const out: string[] = [];
  for (const m of template.matchAll(MERGE_RE)) {
    const name = placeholderName(m);
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

/** Variables in `template` that are not in `allowed`. */
export function unknownMergeVariables(
  template: string,
  allowed: readonly string[] = MERGE_VARIABLES,
): string[] {
  return extractMergeVariables(template).filter((v) => !allowed.includes(v));
}

export type MergeValues = Partial<Record<MergeVariable, string | null | undefined>>;

export interface RenderResult {
  text: string;
  /** Variables that had no value (rendered with their fallback, or removed). */
  missing: string[];
  /** Variables the template uses that are not known merge variables (rendered empty). */
  unknown: string[];
}

/**
 * Render `{{firstName}}` / `{{firstName|φίλε μας}}` placeholders. Missing values use the inline
 * fallback, otherwise they are removed together with a dangling space before punctuation
 * ("Γεια σας {{firstName}}!" → "Γεια σας!"), so a missing name never leaves "Γεια σας !".
 */
export function renderMergeTemplate(template: string, values: MergeValues): RenderResult {
  const missing: string[] = [];
  const unknown: string[] = [];
  let out = '';
  let last = 0;
  for (const m of template.matchAll(MERGE_RE)) {
    const name = placeholderName(m);
    const fallback = m[2]?.trim();
    const start = m.index!;
    out += template.slice(last, start);
    last = start + m[0].length;
    const known = (MERGE_VARIABLES as readonly string[]).includes(name);
    if (!known && !unknown.includes(name)) unknown.push(name);
    const raw = known ? values[name as MergeVariable] : undefined;
    const value = raw == null ? '' : String(raw).trim();
    if (value) {
      out += value;
      continue;
    }
    if (known && !missing.includes(name)) missing.push(name);
    if (fallback) {
      out += fallback;
      continue;
    }
    // Remove the placeholder; tidy the whitespace it leaves behind.
    const next = template.charAt(last);
    if (/[,.!;:?·)]/.test(next) || next === '' || next === '\n') {
      out = out.replace(/[ \t]+$/, '');
    } else if (/[ \t]$/.test(out) || out === '' || out.endsWith('\n')) {
      // "{{firstName}} καλώς ήρθατε" at line start → drop the following space.
      if (next === ' ' || next === '\t') last += 1;
    }
    // "{{firstName}}, ..." at the very start of a line → drop the orphan comma too.
    if ((out === '' || out.endsWith('\n')) && template.charAt(last) === ',') {
      last += 1;
      if (template.charAt(last) === ' ') last += 1;
    }
  }
  out += template.slice(last);
  return { text: out, missing, unknown };
}

/**
 * Opt-out line of a marketing (campaign/automation) SMS when the business set no custom one.
 * GSM-7 Greek capitals, so it never switches a GSM-7 message to UCS-2.
 */
export const DEFAULT_SMS_OPT_OUT_TEXT: Readonly<Record<'el' | 'en', string>> = {
  el: `${toGreekGsmCapitals('Διαγραφή')}: {{unsubscribeLink}}`,
  en: 'Opt out: {{unsubscribeLink}}',
};

/** True when a template already carries the unsubscribe link itself. */
export function usesUnsubscribeLink(template: string): boolean {
  return /\{\{\s*unsubscribeLink\s*(?:\|[^{}]*)?\}\}/.test(template);
}

/**
 * The opt-out line a marketing SMS carries: the business's custom text or the default, with the
 * unsubscribe link filled in (appended when a legacy custom text has no `{{unsubscribeLink}}`).
 * Shared by the API (what is sent and estimated) and the composer preview.
 */
export function smsOptOutLine(
  custom: string | null | undefined,
  locale: 'el' | 'en',
  unsubscribeUrl: string,
): string {
  const template = custom?.trim() || DEFAULT_SMS_OPT_OUT_TEXT[locale];
  let line = renderMergeTemplate(template, { unsubscribeLink: unsubscribeUrl }).text.trim();
  if (unsubscribeUrl && !line.includes(unsubscribeUrl)) line = `${line} ${unsubscribeUrl}`.trim();
  return line;
}

const PROTECTED_RE = /(\{\{[^}]*\}\}|https?:\/\/\S+)/g;

/** `{{name|fallback}}` with the fallback converted to GSM-7 capitals. */
function capitalizeFallbacks(template: string): string {
  return template.replace(
    /\{\{\s*([A-Za-z]+)\s*\|([^}]*)\}\}/g,
    (_m, name: string, fb: string) => `{{${name}|${toGreekGsmCapitals(fb)}}}`,
  );
}

/**
 * "Μετατροπή σε κεφαλαία χωρίς τόνους" for a template: converts the text (and placeholder
 * fallbacks) to GSM-7 Greek capitals but leaves placeholder names and links untouched (an
 * uppercased URL would break).
 */
export function toGreekCapitalsTemplate(template: string): string {
  return template
    .split(PROTECTED_RE)
    .map((part, i) =>
      i % 2 === 1
        ? part.startsWith('{{')
          ? capitalizeFallbacks(part)
          : part
        : toGreekGsmCapitals(part),
    )
    .join('');
}

/**
 * SMS personalization that keeps a GSM-7 message GSM-7: when the template's own text (without
 * placeholders) is GSM-7, merged values and fallbacks are converted to Greek capitals too —
 * otherwise one lowercase name ("Μαρία") would switch the whole SMS to UCS-2 and double its
 * cost. Templates that are already UCS-2 are rendered unchanged.
 */
export function renderSmsTemplate(template: string, values: MergeValues): RenderResult {
  const own = template.replace(MERGE_RE, '');
  if (analyzeSms(own).encoding !== 'GSM-7') return renderMergeTemplate(template, values);
  const safe: MergeValues = {};
  for (const [k, v] of Object.entries(values) as [MergeVariable, string | null | undefined][])
    safe[k] = typeof v === 'string' ? toGreekGsmCapitals(v) : v;
  return renderMergeTemplate(capitalizeFallbacks(template), safe);
}

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ESCAPES[c]!);
}

function safeUrl(url: string): string | null {
  return /^https?:\/\/[^\s<>"']+$/i.test(url) ? url : null;
}

function inlineFormat(escaped: string, linkStyle: string): string {
  // [label](https://…) → <a>, then bare URLs, then **bold** and _italic_.
  let s = escaped.replace(/\[([^\]]{1,200})\]\((https?:\/\/[^)\s]+)\)/g, (_m, label, url) => {
    const u = safeUrl(url.replace(/&amp;/g, '&'));
    return u ? `<a href="${escapeHtml(u)}" style="${linkStyle}">${label}</a>` : label;
  });
  s = s.replace(/(^|[\s(])(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g, (_m, pre, url) => {
    const u = safeUrl(url.replace(/&amp;/g, '&'));
    return u ? `${pre}<a href="${escapeHtml(u)}" style="${linkStyle}">${url}</a>` : `${pre}${url}`;
  });
  s = s.replace(/\*\*([^*\n]{1,500})\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[\s(])_([^_\n]{1,500})_(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  return s;
}

/**
 * Simple rich formatting for email bodies: paragraphs (blank line), line breaks, **bold**,
 * _italic_, [label](https://url) and bare http(s) links. Everything else is escaped, so the
 * output is safe to inject as HTML.
 */
export function formatEmailHtml(
  text: string,
  opts: { linkStyle?: string; paragraphStyle?: string } = {},
): string {
  const linkStyle = opts.linkStyle ?? 'color:#2D6A88;text-decoration:underline;';
  const pStyle = opts.paragraphStyle ?? 'margin:0 0 16px 0;';
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(
      (p) =>
        `<p style="${pStyle}">${inlineFormat(escapeHtml(p), linkStyle).replace(/\n/g, '<br>')}</p>`,
    )
    .join('\n');
}

/** Plain-text version of the formatted body (markers removed, links as "label (url)"). */
export function emailPlainText(text: string): string {
  return text
    .replace(/\[([^\]]{1,200})\]\((https?:\/\/[^)\s]+)\)/g, '$1 ($2)')
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,;:!?]|$)/g, '$1$2');
}

const SENDER_TRANSLIT: Record<string, string> = {
  α: 'a',
  β: 'v',
  γ: 'g',
  δ: 'd',
  ε: 'e',
  ζ: 'z',
  η: 'i',
  θ: 'th',
  ι: 'i',
  κ: 'k',
  λ: 'l',
  μ: 'm',
  ν: 'n',
  ξ: 'x',
  ο: 'o',
  π: 'p',
  ρ: 'r',
  σ: 's',
  ς: 's',
  τ: 't',
  υ: 'y',
  φ: 'f',
  χ: 'ch',
  ψ: 'ps',
  ω: 'o',
};

/**
 * Greek mobile operators accept alphanumeric sender IDs of up to 11 Latin letters/digits (or a
 * numeric sender of up to 13 digits). Greek names are transliterated ("Κουρείο Αλέξης" →
 * "KoureioAlex" is avoided: words are kept, then truncated on a word boundary when possible).
 */
export function normalizeSmsSenderId(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (/^\+?\d{3,13}$/.test(trimmed)) return trimmed.replace(/^\+/, '');
  let latin = '';
  const stripped = trimmed
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/(ου|αυ|ευ)/gi, (m) => {
      const t = ({ ου: 'ou', αυ: 'av', ευ: 'ev' } as Record<string, string>)[m.toLowerCase()]!;
      if (m === m.toLowerCase()) return t;
      return m === m.toUpperCase() ? t.toUpperCase() : t.charAt(0).toUpperCase() + t.slice(1);
    });
  for (const ch of stripped) {
    const lower = ch.toLowerCase();
    const t = SENDER_TRANSLIT[lower];
    if (t) latin += ch === lower ? t : t.charAt(0).toUpperCase() + t.slice(1);
    else latin += ch;
  }
  const words = latin
    .replace(/[^A-Za-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return null;
  let out = '';
  for (const w of words) {
    const candidate = out ? `${out} ${w}` : w;
    if (candidate.length <= 11) out = candidate;
    else break;
  }
  if (!out) out = words[0]!.slice(0, 11);
  return out;
}

/** "m***@example.com", "+30 69•• ••• 123" — for public pages and recipient lists. */
export function maskAddress(channel: Channel, value: string | null | undefined): string | null {
  if (!value) return null;
  if (channel === 'email') {
    const [user = '', domain = ''] = value.split('@');
    return `${user.slice(0, 1)}${'•'.repeat(Math.max(1, Math.min(4, user.length - 1)))}@${domain}`;
  }
  const digits = value.replace(/\s/g, '');
  return `${digits.slice(0, Math.min(6, digits.length - 3))}${'•'.repeat(Math.max(0, digits.length - 9))}•••${digits.slice(-3)}`;
}

// ─── API schemas ─────────────────────────────────────────────────────────────

const IsoDate = z.iso.datetime({ offset: true });
const Uuid = z.uuid();

export const SmsSummarySchema = z.object({
  encoding: z.enum(['GSM-7', 'UCS-2']),
  characters: z.number().int(),
  units: z.number().int(),
  segments: z.number().int(),
  nonGsmCharacters: z.array(z.string()),
});
export type SmsSummary = z.infer<typeof SmsSummarySchema>;

export const PreviewRequestSchema = z.object({
  channel: ChannelSchema,
  subject: z.string().max(200).optional(),
  body: z.string().max(MAX_EMAIL_BODY).default(''),
  /** Designed email (email channel): the preview renders it for the contact. */
  design: EmailDesignSchema.nullable().optional(),
  contactId: Uuid.optional(),
  /** The campaign's language (what the send renders every recipient in). */
  locale: z.enum(['el', 'en']).optional(),
  /** Forms engine: render as a campaign linked to this form (`{{formLink}}` → preview link). */
  formId: Uuid.optional(),
});
export type PreviewRequest = z.input<typeof PreviewRequestSchema>;

export const PreviewResponseSchema = z.object({
  contact: z.object({ id: Uuid, name: z.string() }).nullable(),
  subject: z.string().nullable(),
  text: z.string(),
  html: z.string().nullable(),
  sms: SmsSummarySchema.nullable(),
  missingVariables: z.array(z.string()),
  unknownVariables: z.array(z.string()),
});
export type PreviewResponse = z.infer<typeof PreviewResponseSchema>;

export const QuietHoursInfoSchema = z.object({
  start: z.string(),
  end: z.string(),
  timezone: z.string(),
  active: z.boolean(),
  resumesAt: IsoDate.nullable(),
});
export type QuietHoursInfo = z.infer<typeof QuietHoursInfoSchema>;

export const EstimateResponseSchema = z.object({
  channel: ChannelSchema,
  targeted: z.number().int(),
  eligible: z.number().int(),
  excluded: z.record(z.string(), z.number().int()),
  /** Credits needed (SMS segments summed over recipients; 1 per email). */
  units: z.number().int(),
  wallet: z.object({ available: z.number().int() }),
  sufficient: z.boolean(),
  shortfall: z.number().int(),
  sms: z
    .object({
      encoding: z.enum(['GSM-7', 'UCS-2', 'mixed']),
      minSegments: z.number().int(),
      maxSegments: z.number().int(),
      ucs2Recipients: z.number().int(),
      /** Same message converted to Greek capitals without accents (GSM-7). */
      capitals: z.object({ units: z.number().int(), saved: z.number().int() }),
      tooLong: z.boolean(),
    })
    .nullable(),
  highVolume: z.boolean(),
  highVolumeThreshold: z.number().int(),
  maxRecipients: z.number().int(),
  quietHours: QuietHoursInfoSchema,
  unknownVariables: z.array(z.string()),
});
export type EstimateResponse = z.infer<typeof EstimateResponseSchema>;

export const TestSendResponseSchema = z.object({
  deliveryId: Uuid,
  status: z.enum(DELIVERY_STATUSES),
  creditsCharged: z.number().int(),
  units: z.number().int(),
  replayed: z.boolean(),
});
export type TestSendResponse = z.infer<typeof TestSendResponseSchema>;

export const AudienceContactSchema = z.object({
  id: Uuid,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  tags: z.array(z.string()),
  consentEmail: z.enum(['granted', 'denied', 'unknown']),
  consentSms: z.enum(['granted', 'denied', 'unknown']),
});
export type AudienceContact = z.infer<typeof AudienceContactSchema>;
export const AudienceContactsResponseSchema = z.object({ items: z.array(AudienceContactSchema) });

export const AudienceTagsResponseSchema = z.object({
  items: z.array(z.object({ tag: z.string(), count: z.number().int() })),
});

export const DeliveryRowSchema = z.object({
  id: Uuid,
  contactId: Uuid.nullable(),
  contactName: z.string().nullable(),
  to: z.string().nullable(),
  channel: ChannelSchema,
  purpose: z.enum(DELIVERY_PURPOSES),
  status: z.enum(DELIVERY_STATUSES),
  segments: z.number().int(),
  creditsCharged: z.number().int(),
  errorCode: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: IsoDate,
  acceptedAt: IsoDate.nullable(),
  deliveredAt: IsoDate.nullable(),
  failedAt: IsoDate.nullable(),
  openedAt: IsoDate.nullable(),
  clickedAt: IsoDate.nullable(),
  unsubscribed: z.boolean(),
});
export type DeliveryRow = z.infer<typeof DeliveryRowSchema>;

export const DeliveriesQuerySchema = z.object({
  status: z.enum(['all', 'pending', 'delivered', 'problem', 'excluded', 'engaged']).default('all'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const DeliveriesResponseSchema = z.object({
  items: z.array(DeliveryRowSchema),
  nextCursor: z.string().nullable(),
});

/** Dev/test only: simulate a provider status callback for a delivery (fake providers). */
export const SimulateEventRequestSchema = z.object({
  deliveryId: Uuid,
  type: z.enum([
    'sent',
    'delivered',
    'bounced',
    'failed',
    'opened',
    'clicked',
    'unsubscribed',
    'complained',
  ]),
});
export type SimulateEventType = z.infer<typeof SimulateEventRequestSchema>['type'];

// ─── public unsubscribe (/u/:token) ──────────────────────────────────────────

export const UnsubscribeChannelStateSchema = z.object({
  channel: ChannelSchema,
  address: z.string().nullable(),
  subscribed: z.boolean(),
  /** Bounced/complaint suppressions cannot be lifted from the public page. */
  canResubscribe: z.boolean(),
});

export const UnsubscribePageResponseSchema = z.object({
  businessName: z.string(),
  locale: z.enum(['el', 'en']),
  firstName: z.string().nullable(),
  /** Channel the link arrived through (pre-selected). */
  via: ChannelSchema.nullable(),
  channels: z.array(UnsubscribeChannelStateSchema),
  removed: z.boolean(),
});
export type UnsubscribePageResponse = z.infer<typeof UnsubscribePageResponseSchema>;

export const PublicUnsubscribeRequestSchema = z.object({
  channel: z.enum(['email', 'sms', 'all']).default('all'),
});

/** Consent statement shown on /u/:token before re-subscribing (stored verbatim as evidence). */
export const RESUBSCRIBE_STATEMENT = {
  el: 'Θέλω να λαμβάνω ξανά ενημερώσεις και προσφορές από την επιχείρηση σε αυτό το κανάλι. Μπορώ να διαγραφώ οποιαδήποτε στιγμή.',
  en: 'I want to receive news and offers from this business on this channel again. I can unsubscribe at any time.',
} as const;

export const PublicResubscribeRequestSchema = z.object({
  channel: ChannelSchema,
  /** The user ticked the consent statement shown on the page. */
  confirm: z.literal(true),
  /** Locale of the statement the user saw (stored as consent evidence). */
  statementLocale: z.enum(['el', 'en']).default('el'),
});
